// src/pages/company/FraudAlertsPage.jsx
import React, { useEffect, useState, useRef } from "react";
import CompanyLayout from "../../components/company/CompanyLayout";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { toast } from "react-hot-toast";
import {
  AlertTriangle,
  ShieldAlert,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  Send,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { pressable } from "../../utils/pressable";

// Risk score badge color
const getRiskColor = (score) => {
  if (score >= 80) return "bg-red-50 text-red-700 border-red-200";
  if (score >= 50) return "bg-[#FBF3E2] text-gold-ink border-[#EAD9B0]";
  return "bg-[#E7F2EC] text-success-text border-[#C7E3D3]";
};

const getStatusBadge = (status) => {
  switch (status) {
    case "pending":
      return "bg-[#FBF3E2] text-gold-ink";
    case "investigating":
      return "bg-blue-50 text-blue-700";
    case "resolved":
      return "bg-[#E7F2EC] text-success-text";
    case "dismissed":
      return "bg-sunken text-faint";
    default:
      return "bg-sunken text-faint";
  }
};

export default function FraudAlertsPage() {
  const [org, setOrg] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("alerts"); // alerts | trend | appeal
  
  // Appeal form state
  const [appealDisputeId, setAppealDisputeId] = useState("");
  const [appealReason, setAppealReason] = useState("");
  const [appealSubmitting, setAppealSubmitting] = useState(false);
  
  // Expanded alert details
  const [expandedAlertId, setExpandedAlertId] = useState(null);
  
  // WebSocket ref
  const socketRef = useRef(null);

  // Stats derived from alerts
  const stats = {
    total: alerts.length,
    pending: alerts.filter((a) => a.status === "pending").length,
    investigating: alerts.filter((a) => a.status === "investigating").length,
    resolved: alerts.filter((a) => a.status === "resolved").length,
    highRisk: alerts.filter((a) => (a.risk_score || 0) >= 80).length,
  };

  useEffect(() => {
    loadOrg();
    loadAlerts();
    loadTrendData();
    setupWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, []);

  const loadOrg = async () => {
    try {
      const me = await fetchWithAuth("/api/accounts/me/");
      setOrg(me?.organization || null);
    } catch {
      setOrg(null);
    }
  };

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth("/api/contracts/fraud-alerts/");
      const data = res?.json ? await res.json() : res;
      const items = Array.isArray(data) ? data : data?.results || [];
      setAlerts(items);
    } catch (e) {
      console.error("Failed to load fraud alerts:", e);
      // Don't show error toast - might just be no alerts
    } finally {
      setLoading(false);
    }
  };

  const loadTrendData = async () => {
    try {
      const res = await fetchWithAuth("/api/analytics/fraud-trend/");
      const data = res?.json ? await res.json() : res;
      setTrendData(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load fraud trend data:", e);
    }
  };

  const setupWebSocket = () => {
    try {
      const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
      const ws = new WebSocket(`${wsProtocol}://${window.location.host}/ws/fraud-alerts/`);

      ws.onmessage = (event) => {
        try {
          const newAlert = JSON.parse(event.data);
          setAlerts((prev) => {
            // Check if alert already exists
            const exists = prev.some((a) => a.id === newAlert.id);
            if (exists) {
              return prev.map((a) => (a.id === newAlert.id ? newAlert : a));
            }
            return [newAlert, ...prev];
          });
          toast.error(`New fraud alert: ${newAlert.reason || "Suspicious activity detected"}`, {
            icon: "🚨",
            duration: 5000,
          });
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onerror = () => {
        console.log("WebSocket connection failed - alerts won't update in real-time");
      };

      socketRef.current = ws;
    } catch {
      // WebSocket not available
    }
  };

  const handleUpdateStatus = async (alertId, newStatus) => {
    try {
      await fetchWithAuth(`/api/contracts/fraud-alerts/${alertId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success(`Alert marked as ${newStatus}`);
      loadAlerts();
    } catch {
      toast.error("Failed to update alert status");
    }
  };

  const handleSubmitAppeal = async (e) => {
    e.preventDefault();
    if (!appealDisputeId || !appealReason.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setAppealSubmitting(true);
    try {
      await fetchWithAuth("/api/contracts/appeal-fraud/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dispute_id: appealDisputeId,
          reason: appealReason.trim(),
        }),
      });
      toast.success("Appeal submitted successfully");
      setAppealDisputeId("");
      setAppealReason("");
      setActiveTab("alerts");
    } catch (e) {
      toast.error(e?.message || "Failed to submit appeal");
    } finally {
      setAppealSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const header = (
    <div className="flex items-center gap-3 text-ink">
      <ShieldAlert size={24} />
      <h1 className="text-3xl font-display italic text-ink">Fraud alerts</h1>
    </div>
  );

  return (
    <CompanyLayout org={org} header={header}>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-surface border border-line rounded-2xl p-4">
          <div className="flex items-center gap-2 text-sm text-tertiary mb-1">
            <AlertTriangle size={14} />
            Total Alerts
          </div>
          <div className="text-2xl font-bold text-ink">{stats.total}</div>
        </div>
        <div className="bg-surface border border-line rounded-2xl p-4">
          <div className="flex items-center gap-2 text-sm text-gold-ink mb-1">
            <Clock size={14} />
            Pending
          </div>
          <div className="text-2xl font-bold text-gold-ink">{stats.pending}</div>
        </div>
        <div className="bg-surface border border-line rounded-2xl p-4">
          <div className="flex items-center gap-2 text-sm text-blue-700 mb-1">
            <AlertCircle size={14} />
            Investigating
          </div>
          <div className="text-2xl font-bold text-blue-700">{stats.investigating}</div>
        </div>
        <div className="bg-surface border border-line rounded-2xl p-4">
          <div className="flex items-center gap-2 text-sm text-success-text mb-1">
            <CheckCircle size={14} />
            Resolved
          </div>
          <div className="text-2xl font-bold text-success-text">{stats.resolved}</div>
        </div>
        <div className="bg-surface border border-line rounded-2xl p-4">
          <div className="flex items-center gap-2 text-sm text-red-700 mb-1">
            <ShieldAlert size={14} />
            High Risk
          </div>
          <div className="text-2xl font-bold text-red-700">{stats.highRisk}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { key: "alerts", label: "Alerts", icon: AlertTriangle },
          { key: "trend", label: "Risk Trend", icon: TrendingUp },
          { key: "appeal", label: "Submit Appeal", icon: FileText },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-pill text-sm transition-colors ${
              activeTab === key
                ? "bg-brand text-white font-medium hover:bg-brand-hover"
                : "bg-surface border border-line-strong text-ink hover:border-brand hover:text-brand"
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* Alerts Tab */}
      {activeTab === "alerts" && (
        <div className="bg-surface border border-line rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-tertiary">Loading alerts...</div>
          ) : alerts.length === 0 ? (
            <div className="p-8 text-center">
              <ShieldAlert size={48} className="mx-auto text-faint mb-3" />
              <div className="text-tertiary">No fraud alerts</div>
              <div className="text-sm text-faint mt-1">
                You'll be notified when suspicious activity is detected
              </div>
            </div>
          ) : (
            <div className="divide-y divide-line">
              {alerts.map((alert) => (
                <div key={alert.id} className="p-4 hover:bg-sunken">
                  {/* Alert Header */}
                  <div
                    className="flex items-start justify-between cursor-pointer"
                    {...pressable(() =>
                      setExpandedAlertId(expandedAlertId === alert.id ? null : alert.id)
                    )}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-0.5 rounded text-xs border ${getRiskColor(alert.risk_score || 0)}`}>
                          Risk: {alert.risk_score || 0}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadge(alert.status)}`}>
                          {alert.status || "pending"}
                        </span>
                        <span className="text-xs text-faint">
                          {formatDate(alert.timestamp || alert.created_at)}
                        </span>
                      </div>
                      <div className="text-ink font-medium">
                        Dispute #{alert.dispute_id || alert.id}
                      </div>
                      <div className="text-sm text-secondary mt-1">{alert.reason}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {expandedAlertId === alert.id ? (
                        <ChevronUp size={20} className="text-tertiary" />
                      ) : (
                        <ChevronDown size={20} className="text-tertiary" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedAlertId === alert.id && (
                    <div className="mt-4 pt-4 border-t border-line">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <div className="text-xs text-faint">Disputed By</div>
                          <div className="text-sm text-ink">{alert.disputed_by || "-"}</div>
                        </div>
                        <div>
                          <div className="text-xs text-faint">Contract ID</div>
                          <div className="text-sm text-ink">{alert.contract_id || "-"}</div>
                        </div>
                        <div>
                          <div className="text-xs text-faint">Amount</div>
                          <div className="text-sm text-ink">
                            {alert.amount
                              ? new Intl.NumberFormat("en-US", {
                                  style: "currency",
                                  currency: "USD",
                                }).format(alert.amount)
                              : "-"}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-faint">Detection Method</div>
                          <div className="text-sm text-ink">{alert.detection_method || "Automated"}</div>
                        </div>
                      </div>

                      {alert.details && (
                        <div className="mb-4">
                          <div className="text-xs text-faint mb-1">Details</div>
                          <div className="text-sm text-secondary bg-sunken rounded p-3">
                            {alert.details}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2">
                        {alert.status === "pending" && (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateStatus(alert.id, "investigating");
                              }}
                              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-pill"
                            >
                              Start Investigation
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateStatus(alert.id, "dismissed");
                              }}
                              className="px-3 py-1.5 text-xs border border-line-strong bg-surface text-ink hover:border-brand hover:text-brand rounded-pill"
                            >
                              Dismiss
                            </button>
                          </>
                        )}
                        {alert.status === "investigating" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateStatus(alert.id, "resolved");
                            }}
                            className="px-3 py-1.5 text-xs bg-[#16754C] hover:bg-[#125F3E] text-white rounded-pill"
                          >
                            Mark Resolved
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Trend Tab */}
      {activeTab === "trend" && (
        <div className="bg-surface border border-line rounded-2xl p-6">
          <h3 className="text-lg font-display italic text-ink mb-4">Fraud risk score over time</h3>
          {trendData.length === 0 ? (
            <div className="text-center text-tertiary py-8">
              No trend data available yet
            </div>
          ) : (
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                  <XAxis
                    dataKey="date"
                    stroke="#B8B4AC"
                    tick={{ fill: "#8A867E", fontSize: 12 }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    stroke="#B8B4AC"
                    tick={{ fill: "#8A867E", fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #E5E2DC",
                      borderRadius: "8px",
                    }}
                    labelStyle={{ color: "#1A1A1A" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="avg_score"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    fill="url(#colorScore)"
                    name="Risk Score"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Appeal Tab */}
      {activeTab === "appeal" && (
        <div className="bg-surface border border-line rounded-2xl p-6">
          <h3 className="text-lg font-display italic text-ink mb-4">Appeal a fraud flag</h3>
          <p className="text-sm text-secondary mb-6">
            If you believe a fraud flag was raised in error, you can submit an appeal with your explanation.
          </p>

          <form onSubmit={handleSubmitAppeal} className="space-y-4 max-w-xl">
            <div>
              <label htmlFor="fraudalertspage-dispute-id" className="block text-sm text-secondary mb-1">Dispute ID</label>
              <input id="fraudalertspage-dispute-id"
                type="text"
                value={appealDisputeId}
                onChange={(e) => setAppealDisputeId(e.target.value)}
                placeholder="Enter the dispute ID"
                className="w-full bg-surface border border-line-control rounded px-4 py-2 text-ink placeholder-faint focus:border-brand focus:outline-none"
                required
              />
            </div>

            <div>
              <label htmlFor="fraudalertspage-reason-for-appeal" className="block text-sm text-secondary mb-1">Reason for Appeal</label>
              <textarea id="fraudalertspage-reason-for-appeal"
                value={appealReason}
                onChange={(e) => setAppealReason(e.target.value)}
                placeholder="Explain why you believe this fraud flag is incorrect..."
                rows={6}
                className="w-full bg-surface border border-line-control rounded px-4 py-2 text-ink placeholder-faint resize-none focus:border-brand focus:outline-none"
                required
              />
            </div>

            <button
              type="submit"
              disabled={appealSubmitting}
              className={`flex items-center gap-2 px-4 py-2 rounded-pill font-medium ${
                appealSubmitting
                  ? "bg-sunken text-faint cursor-not-allowed"
                  : "bg-brand text-white hover:bg-brand-hover"
              }`}
            >
              <Send size={16} />
              {appealSubmitting ? "Submitting..." : "Submit Appeal"}
            </button>
          </form>
        </div>
      )}
    </CompanyLayout>
  );
}